// Survivor look (CHANGELOG 8): a male or a female static picture. In the MVP the
// picture never changes with gear; equipped items only show in their slots.
//
// Files (copied from assets/characters/webp by `pnpm icons:sync`):
//   survivor_{bodyType}_{1024|512}.webp          full figure (desktop / mobile)
//   survivor_{bodyType}_portrait_{256|128}.webp  profile, report, share card / HUD, lists

import { NPC_NAME_LIST } from './content/world';

export const BODY_TYPES = ['male', 'female'] as const;
export type BodyType = (typeof BODY_TYPES)[number];

export const CHARACTER_SIZES = [1024, 512] as const;
export type CharacterSize = (typeof CHARACTER_SIZES)[number];
export const PORTRAIT_SIZES = [256, 128] as const;
export type PortraitSize = (typeof PORTRAIT_SIZES)[number];

/** Full figures are 2:3 (688 × 1024 and 344 × 512). */
export const CHARACTER_ASPECT = 688 / 1024;

export function characterImage(bodyType: BodyType, size: CharacterSize): string {
  return `/assets/characters/survivor_${bodyType}_${size}.webp`;
}

export function characterPortrait(bodyType: BodyType, size: PortraitSize): string {
  return `/assets/characters/survivor_${bodyType}_portrait_${size}.webp`;
}

/** Every file the client expects, for the asset check. */
export const CHARACTER_FILES: string[] = BODY_TYPES.flatMap((b) => [
  ...CHARACTER_SIZES.map((s) => characterImage(b, s)),
  ...PORTRAIT_SIZES.map((s) => characterPortrait(b, s)),
]);

/** NPC survivor names by gender (content/world NPC_NAME_LIST, plan v0.6 B2). */
const NPC_GENDER = new Map(NPC_NAME_LIST.map((n) => [n.name.toLowerCase(), n.gender]));

/**
 * A look for someone whose body type is not known (NPC survivors, old reports and
 * share cards): known NPC names get the matching picture, anything else is fixed per
 * seed, so the same name always gets the same picture.
 */
export function fallbackBodyType(seed: string): BodyType {
  const g = NPC_GENDER.get(seed.trim().toLowerCase());
  if (g) return g === 'f' ? 'female' : 'male';
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return BODY_TYPES[Math.abs(h) % BODY_TYPES.length];
}

/** The body type to show: the stored one, or the fallback for old data. */
export function bodyTypeOf(bodyType: string | null | undefined, seed: string): BodyType {
  return bodyType === 'male' || bodyType === 'female' ? bodyType : fallbackBodyType(seed);
}
